import { BaseService } from "./BaseService";
import {
  AdminTenantSubscriptionOverviewFilters,
  AdminTenantSubscriptionOverviewResponse,
  TenantMergedFeaturesResponse,
  TenantSubscription,
} from "@/types/tenant-subscription";
import type { ListTenantsResponse } from "@/types/tenant-catalog";

function toQueryParams(
  filters?: AdminTenantSubscriptionOverviewFilters,
): Record<string, string> | undefined {
  if (filters === undefined) {
    return undefined;
  }

  const params = Object.fromEntries(
    Object.entries(filters)
      .filter(
        ([, value]) => value !== undefined && value !== null && value !== "",
      )
      .map(([key, value]) => [key, String(value)]),
  );

  return Object.keys(params).length > 0 ? params : undefined;
}

class AdminTenantSubscriptionsService extends BaseService {
  private readonly basePath = "/admin/subscriptions/tenants";

  constructor() {
    super();
  }

  getTenantOptions(): Promise<ListTenantsResponse> {
    return this.get<ListTenantsResponse>(`${this.basePath}/options`);
  }

  getAllTenantSubscriptions(
    filters?: AdminTenantSubscriptionOverviewFilters,
  ): Promise<AdminTenantSubscriptionOverviewResponse> {
    return this.get<AdminTenantSubscriptionOverviewResponse>(
      this.basePath,
      toQueryParams(filters),
    );
  }

  async getTenantSubscriptions(
    tenantId: string,
  ): Promise<TenantSubscription[]> {
    const raw = await this.get<TenantSubscription[]>(
      `${this.basePath}/${tenantId}`,
    );
    return Array.isArray(raw) ? raw : [];
  }

  getTenantMergedFeatures(
    tenantId: string,
  ): Promise<TenantMergedFeaturesResponse> {
    return this.get<TenantMergedFeaturesResponse>(
      `${this.basePath}/${tenantId}/active`,
    );
  }

  assignPlan(tenantId: string, planId: string): Promise<TenantSubscription> {
    const qs = new URLSearchParams({ planId }).toString();
    return this.post<TenantSubscription>(`${this.basePath}/${tenantId}?${qs}`);
  }

  cancelSubscription(
    tenantId: string,
    subscriptionId: string,
    cancelAtPeriodEnd = true,
  ): Promise<void> {
    const qs = new URLSearchParams({
      cancelAtPeriodEnd: String(cancelAtPeriodEnd),
    }).toString();

    return this.delete<void>(
      `${this.basePath}/${tenantId}/${subscriptionId}?${qs}`,
    );
  }
}

export const adminTenantSubscriptionsService =
  new AdminTenantSubscriptionsService();
